import { useNavigate } from 'react-router';
import { getJSONItem } from '../../shared/services/storage/storage';
import { STORAGE_KEY } from '../../shared/config';
import { useContext, useState } from 'react';
import { AuthB2cContext } from '../context/AuthB2cContext';
import api from '../../shared/services/api/api';
import type { State } from '../types';
import Loading from '../components/Loading';
import PageContainer from '../components/PageContainer';
import { useTitle } from '../../shared/hooks/useTitle';
import NavigationButtons from '../components/NavigationButtons';
import { useHotkeys } from 'react-hotkeys-hook';
import PageHeader from '../components/PageHeader';
import {
  CARRIER_KEY,
  DATA_ENTRY_SEVENTH_STEP_ROUTE,
  THANKS_KEY,
  createPathForRoute,
} from '../constants/baseRoutePaths';
import { scrollToConf } from '../constants/scrollToConf';
import GeneralError from '../components/GeneralError';
import { pageSubtitles, pageTitles } from '../constants/fields';

const preparePayloadForConfermaRitiro = (state: State) => {
  const payload: Record<string, string | number> = {
    data_ritiro: state['Data'].value,
    anagrafica: state['Nome e cognome (c/o)'].value,
    indirizzo: state['Indirizzo'].value,
    cap: state['CAP'].value,
    citta: state['Città'].value,
    provincia: state['Provincia'].value,
    telefono: state['Telefono'].value,
    email: state['Email'].value,
  };

  return payload;
};

const formatDate = (date: string) => {
  const parsedDate = new Date(date);
  return parsedDate.toLocaleDateString('it-IT', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const Carrier = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const navigate = useNavigate();
  const state = getJSONItem(STORAGE_KEY.B2C_STATE, null);
  const authB2cContext = useContext(AuthB2cContext);
  const payload = preparePayloadForConfermaRitiro(state);
  const title = pageTitles[CARRIER_KEY];
  const subtitle = typeof pageSubtitles[CARRIER_KEY] === 'string' ? pageSubtitles[CARRIER_KEY] : '';

  useTitle(title);

  const handleSendData = async () => {
    const parameters = {
      visitId: Number(authB2cContext?.user?.visitId),
      accessToken: authB2cContext?.user?.access_token || '',
      payload,
    };
    setLoading(true);
    const response = await api.pickUpConfirmation(parameters).catch((error) => {
      console.error(error);
      setLoading(false);
      setError(true);
    });
    if (response.status === 200 && response.success === true && response.tnt_pdf !== '') {
      setError(false);
      window.scrollTo(scrollToConf);
      const pdfLink = encodeURIComponent(response.tnt_pdf);
      navigate(`${createPathForRoute(THANKS_KEY)}/${pdfLink}`);
    }
    setLoading(false);
    setError(true);
  };

  useHotkeys('enter', () => handleSendData());
  useHotkeys('shift+enter', () => navigate(createPathForRoute(DATA_ENTRY_SEVENTH_STEP_ROUTE)));

  if (loading) {
    return <Loading />;
  }

  return (
    <PageContainer>
      <>
        <PageHeader title={title} subtitle={subtitle} />
        <div className="flex flex-wrap justify-between my-4">
          <div className="p-0 w-full xl:w-6/12 xl:odd:pr-2 xl:even:pl-2">
            <fieldset className="my-0.5 h-full rounded-md border-2 border-solid border-black-600 p-2">
              <legend className="font-bold">Data e ora</legend>
              <div>
                <span className="font-bold">Data:</span> {formatDate(String(payload['data_ritiro']))}
              </div>
              <div>
                <span className="font-bold">Orario:</span> 9:00-13:00 / 14:00-18:00
              </div>
            </fieldset>
          </div>
          <div className="p-0 w-full xl:w-6/12 xl:odd:pr-2 xl:even:pl-2">
            <fieldset className="my-0.5 h-full rounded-md border-2 border-solid border-black-600 p-2">
              <legend className="font-bold">Nome e indirizzo</legend>
              <div>
                <span className="font-bold">Nome e cognome:</span> <span>{payload['anagrafica']}</span>
              </div>
              <div>
                <span className="font-bold">Indirizzo:</span> <span>{payload['indirizzo']}</span>
              </div>
              <div>
                <span className="font-bold">CAP:</span> <span>{payload['cap']}</span>
                <div>
                  <span className="font-bold">Città:</span> <span>{payload['citta']}</span>
                </div>
              </div>
              <div>
                <span className="font-bold">Provincia:</span> <span>{payload['provincia']}</span>
              </div>
              <div>
                <span className="font-bold">Telefono:</span> <span>{payload['telefono']}</span>
              </div>
              <div>
                <span className="font-bold">Email:</span> <span>{payload['email']}</span>
              </div>
            </fieldset>
          </div>
          <div className="p-0 w-full xl:odd:pr-2 xl:even:pl-2">
            <fieldset className="my-0.5 h-full rounded-md border-2 border-solid border-black-600 p-2">
              <legend className="font-bold">Note</legend>
              <div>
                <p>Il corriere non effettua ritiri nei giorni festivi ed il sabato.</p>
                <p>
                  Se temi di non essere a casa puoi portare il campione in un TNT Point o provvedere tu stesso alla
                  spedizione tramite le poste.
                </p>
              </div>
            </fieldset>
          </div>
        </div>
        {error && (
          <GeneralError
            message={"Si è verificato un errore al salvataggio dei dati. Riprovare o contattare l'assistenza tecnica."}
          />
        )}
        <NavigationButtons
          prev={{
            label: 'Modifica i dati',
            action: () => navigate(createPathForRoute(DATA_ENTRY_SEVENTH_STEP_ROUTE)),
          }}
          next={{
            label: 'Conferma i dati',
            action: () => handleSendData(),
            color: 'success',
          }}
        />
      </>
    </PageContainer>
  );
};

export default Carrier;
